import { useMemo, useRef, useState } from 'react';
import type { AxiosError } from 'axios';
import {
  useApplyFixedPriceImport,
  usePreisToolSuppliers,
  usePreviewFixedPriceImport,
} from '@/api/preis-tool/preis-tool.hooks';
import type {
  FixedPriceImportColumnMapping,
  FixedPriceImportField,
  FixedPriceImportMissingAction,
  FixedPriceImportPreview,
  FixedPriceImportResult,
  FixedPriceImportRowStatus,
  FixedPriceImportSheet,
} from '@/api/preis-tool/preis-tool.types';
import { triggerBlobDownload } from '@/api/utils/download';
import { DsAlert, DsButton, DsCombobox, DsInput, DsModal, DsSegmented, DsTabNav } from '@/modules/internal-v2/ds';
import { newSupplierLabel, supplierGroups } from '../helpers';
import {
  FIELDS,
  learnedColumnNames,
  PriceListReadError,
  readPriceListFile,
  ROW_STATUS,
  targetLabel,
  toCsv,
  type PriceListWorkbook,
} from '../price-list-file';
import { PriceListColumnNamesEditor } from './price-list-column-names.component';
import { PriceListSheetPanel } from './price-list-sheet-panel.component';

interface PriceListImportDialogProps {
  onClose: () => void;
}

/** What the user decided for one sheet; left out = the server's suggestion. */
interface SheetChoice {
  headerRow?: number;
  mapping?: FixedPriceImportColumnMapping;
  include?: boolean;
}

const FILE_PROBLEM: Record<string, string> = {
  UNSUPPORTED_FORMAT: 'Dieses Dateiformat wird nicht unterstützt. Möglich sind xlsx, xls und csv.',
  FILE_TOO_LARGE: 'Die Datei ist zu groß (höchstens 5 MB).',
  UNREADABLE: 'Die Datei konnte nicht gelesen werden.',
  EMPTY: 'Kein Tabellenblatt der Datei enthält Zeilen unter einer Kopfzeile.',
  TOO_MANY_ROWS: 'Die Datei hat zu viele Zeilen (höchstens 5.000 je Blatt und 10.000 insgesamt).',
  TOO_MANY_COLUMNS: 'Die Datei hat zu viele Spalten (höchstens 40).',
  TOO_MANY_SHEETS: 'Die Datei hat zu viele Tabellenblätter (höchstens 20).',
};

function errorText(error: AxiosError<{ problem?: string }> | null): string | null {
  if (!error) return null;
  if (error.response?.status === 413) return FILE_PROBLEM.FILE_TOO_LARGE;
  const problem = error.response?.data?.problem;
  return (problem && FILE_PROBLEM[problem]) || 'Die Preisliste konnte nicht verarbeitet werden.';
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** Same supplier apart from case and spacing — as the backend decides (`supplierKey`). */
const sameSupplier = (a: string, b: string) =>
  a.trim().replace(/\s+/g, ' ').toLowerCase() === b.trim().replace(/\s+/g, ' ').toLowerCase();

/**
 * Upload of a fixed-price supplier's price list (EOS-59): choose supplier and
 * valid-to date, pick an xlsx / xls / csv file — read here in the browser,
 * only its cells go to the server. Every sheet of the file is a tab of its
 * own: imported or not, its header row, which column is used as which field
 * (suggested from the shared column names, changeable per column), and its
 * rows with the article each belongs to. Then decide what happens to the
 * supplier's fixed prices for articles missing in the file and take the
 * matched prices over. Columns assigned by hand can be remembered as column
 * names for the next upload; the names themselves are edited in a second
 * view of the dialog.
 */
export function PriceListImportDialog({ onClose }: PriceListImportDialogProps) {
  const { data: supplierData } = usePreisToolSuppliers();
  const previewMutation = usePreviewFixedPriceImport();
  const applyMutation = useApplyFixedPriceImport();
  const suppliers = supplierData?.suppliers ?? [];
  const supplierOptions = useMemo(() => supplierGroups(supplierData), [supplierData]);

  const [view, setView] = useState<'upload' | 'names'>('upload');
  const [supplierName, setSupplierName] = useState('');
  const [validTo, setValidTo] = useState('');
  const [missingAction, setMissingAction] = useState<FixedPriceImportMissingAction>('keep');
  const [workbook, setWorkbook] = useState<PriceListWorkbook | null>(null);
  const [choices, setChoices] = useState<Record<number, SheetChoice>>({});
  const [activeSheet, setActiveSheet] = useState(0);
  const [remember, setRemember] = useState(true);
  const [reading, setReading] = useState(false);
  const [readError, setReadError] = useState<string | null>(null);
  const [preview, setPreview] = useState<FixedPriceImportPreview | null>(null);
  // The supplier the shown preview was computed for — its outcomes belong to it.
  const [previewSupplier, setPreviewSupplier] = useState('');
  // The supplier of the last preview asked for, so a pick plus leaving the field asks only once.
  const requestedSupplier = useRef('');
  const [result, setResult] = useState<FixedPriceImportResult | null>(null);

  /** Positions in the workbook of the sheets that go to the server, in the server's order. */
  const sentIndexes = useMemo(
    () => (workbook ? workbook.sheets.flatMap((s, i) => (s.problem ? [] : [i])) : []),
    [workbook],
  );
  const shown = result ?? preview;
  const previewOf = (index: number): FixedPriceImportSheet | null => {
    const position = sentIndexes.indexOf(index);
    return position >= 0 ? (shown?.sheets[position] ?? null) : null;
  };

  const requestSheets = (wb: PriceListWorkbook, picked: Record<number, SheetChoice>) =>
    wb.sheets.flatMap((s, i) => (s.problem ? [] : [{ name: s.name, rows: s.rows, ...picked[i] }]));

  const runPreview = (
    wb: PriceListWorkbook,
    picked: Record<number, SheetChoice>,
    supplier = supplierName,
    onFirst?: (p: FixedPriceImportPreview) => void,
  ) => {
    setResult(null);
    requestedSupplier.current = supplier;
    previewMutation.mutate(
      { fileName: wb.fileName, sheets: requestSheets(wb, picked), supplierName: supplier.trim() || undefined },
      {
        onSuccess: (p) => {
          setPreview(p);
          setPreviewSupplier(supplier);
          onFirst?.(p);
        },
        onError: () => setPreview(null),
      },
    );
  };

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    setWorkbook(null);
    setChoices({});
    setActiveSheet(0);
    setPreview(null);
    setResult(null);
    setReadError(null);
    previewMutation.reset();
    if (!file) return;
    setReading(true);
    try {
      const wb = await readPriceListFile(file);
      const sent = wb.sheets.flatMap((s, i) => (s.problem ? [] : [i]));
      setWorkbook(wb);
      // Open the first sheet that will be imported (a summary sheet in front is not).
      runPreview(wb, {}, supplierName, (p) => {
        const first = p.sheets.findIndex((s) => s.included);
        setActiveSheet(sent[first >= 0 ? first : 0] ?? 0);
      });
    } catch (error) {
      setReadError(error instanceof PriceListReadError ? FILE_PROBLEM[error.problem] : FILE_PROBLEM.UNREADABLE);
    } finally {
      setReading(false);
    }
  };

  /**
   * A change to one sheet. An imported sheet stays imported, so one that
   * loses its price column blocks the import instead of dropping out; a sheet
   * left out for unassigned columns comes in once they are assigned.
   */
  const changeSheet = (index: number, change: SheetChoice) => {
    if (!workbook) return;
    const current = previewOf(index);
    const next = {
      ...choices,
      [index]: { ...(current?.included ? { include: true } : {}), ...choices[index], ...change },
    };
    setChoices(next);
    runPreview(workbook, next);
  };

  const onColumnField = (index: number, columnIndex: number, field: FixedPriceImportField | null) => {
    const current = previewOf(index);
    if (!current) return;
    const mapping = { ...current.mapping };
    for (const f of FIELDS) if (mapping[f] === columnIndex) mapping[f] = null;
    if (field) mapping[field] = columnIndex;
    changeSheet(index, { mapping });
  };

  // The outcome per row and the count of fixed prices "sofort beenden" would
  // close depend on the supplier: compute them again for a different one.
  const refreshForSupplier = (name: string) => {
    if (workbook && preview && !result && !sameSupplier(name, requestedSupplier.current)) {
      runPreview(workbook, choices, name);
    }
  };

  const learned = shown && !result ? learnedColumnNames(shown.sheets) : [];

  const apply = () => {
    if (!workbook || !preview) return;
    const name = supplierName.trim();
    const known = suppliers.find((s) => s.name.trim().toLowerCase() === name.toLowerCase());
    applyMutation.mutate(
      {
        fileName: workbook.fileName,
        sheets: requestSheets(workbook, choices),
        supplierName: name,
        supplierBpId: known?.id,
        validTo,
        missingAction,
        rememberColumnNames: remember && learned.length > 0,
      },
      {
        onSuccess: (r) => {
          setResult(r);
          setPreview(r);
          setPreviewSupplier(name);
        },
      },
    );
  };

  const afterNamesSaved = () => {
    setView('upload');
    if (workbook) runPreview(workbook, choices);
  };

  // The outcome per row is shown once it belongs to the supplier in the field
  // (after the import: to the supplier it was done for).
  const showOutcome = !!shown && shown.newCount != null && (!!result || sameSupplier(previewSupplier, supplierName));
  const imported = shown ? shown.sheets.filter((s) => s.included && !s.problem) : [];
  const blocked = shown ? shown.sheets.filter((s) => s.included && s.problem) : [];

  // Rows that were not taken over, for checking them in Excel.
  const downloadNotTaken = () => {
    if (!shown || !workbook) return;
    // At the limit of four: skipped by the import, or ahead of it as the preview says.
    const skipped = new Set(
      result?.skippedItemIds ??
        (showOutcome
          ? imported.flatMap((s) => s.rows.flatMap((r) => (r.outcome === 'skip_limit' && r.itemId ? [r.itemId] : [])))
          : []),
    );
    const reasonOf = (status: FixedPriceImportRowStatus, itemId: string | null) =>
      status === 'matched' && itemId && skipped.has(itemId)
        ? 'Artikel hat schon vier aktive Fixpreise'
        : ROW_STATUS[status].label;
    const de = (n: number | null) => (n == null ? '' : n.toFixed(2).replace('.', ','));
    triggerBlobDownload(
      toCsv(
        [
          'Blatt',
          'Zeile',
          'Artikelnummer',
          'EAN',
          'Bezeichnung',
          'EK netto',
          'UVP netto',
          'UVP brutto',
          'Menge',
          'Grund',
          'Artikel in EOS',
        ],
        imported.flatMap((s) =>
          s.rows
            .filter((r) => r.status !== 'matched' || (r.itemId != null && skipped.has(r.itemId)))
            .map((r) => [
              s.name,
              r.rowNumber,
              r.itemNumber,
              r.ean,
              r.description,
              de(r.netPrice),
              de(r.uvpNet),
              de(r.uvpGross),
              r.quantity,
              reasonOf(r.status, r.itemId),
              r.itemId,
            ]),
        ),
      ),
      `${workbook.fileName.replace(/\.[^.]+$/, '')} - nicht übernommen.csv`,
    );
  };

  // Matched rows skipped at the limit of four, as far as known.
  const skippedCount = result ? result.skippedItemIds.length : showOutcome ? (shown?.skipCount ?? 0) : 0;
  const notTakenCount = shown ? shown.rowCount - shown.matchedCount + skippedCount : 0;
  const toTakeCount = preview ? preview.matchedCount - skippedCount : 0;
  const canApply =
    !!workbook &&
    !!preview &&
    !result &&
    blocked.length === 0 &&
    preview.matchedCount > 0 &&
    !!supplierName.trim() &&
    validTo !== '';
  const busy = reading || previewMutation.isPending || applyMutation.isPending;
  const error =
    readError ??
    errorText(previewMutation.error as AxiosError<{ problem?: string }> | null) ??
    (applyMutation.isError ? 'Die Preise konnten nicht übernommen werden.' : null);
  const missingCount = preview?.missingActiveCount ?? null;
  const multiSheet = (workbook?.sheets.length ?? 0) > 1;
  const activeCells = workbook?.sheets[activeSheet];

  if (view === 'names') {
    return (
      <DsModal
        open
        onClose={onClose}
        size="wide"
        title="Spaltennamen der Preislisten"
        description="Welche Spaltenüberschrift EOS welchem Feld zuordnet"
        footer={
          <DsButton variant="ghost" onClick={() => setView('upload')}>
            Zurück zum Upload
          </DsButton>
        }
      >
        <PriceListColumnNamesEditor onSaved={afterNamesSaved} />
      </DsModal>
    );
  }

  return (
    <DsModal
      open
      onClose={onClose}
      size="wide"
      title="Preisliste hochladen"
      description="Feste Einkaufspreise eines Lieferanten aus einer Excel- oder CSV-Datei übernehmen"
      footer={
        <>
          <DsButton variant="ghost" onClick={onClose} disabled={applyMutation.isPending}>
            Schließen
          </DsButton>
          <DsButton variant="primary" onClick={apply} disabled={!canApply || busy}>
            {applyMutation.isPending
              ? 'Übernehme…'
              : preview && !result
                ? `${toTakeCount} Preis${toTakeCount === 1 ? '' : 'e'} übernehmen`
                : 'Preise übernehmen'}
          </DsButton>
        </>
      }
    >
      <div className="pt-form">
        <div className="pt-field-row">
          <label className="pt-field">
            <span className="pt-label">Lieferant</span>
            <DsCombobox
              value={supplierName}
              onChange={setSupplierName}
              onCommit={refreshForSupplier}
              groups={supplierOptions}
              freeText={{ createLabel: newSupplierLabel }}
              placeholder="Name wählen oder frei eintippen"
              maxLength={200}
            />
          </label>
          <label className="pt-field" style={{ flex: '0 0 190px' }}>
            <span className="pt-label">Gültig bis (alle Zeilen, Pflicht)</span>
            <DsInput type="date" value={validTo} onChange={(e) => setValidTo(e.target.value)} />
          </label>
        </div>
        <div className="pt-field-row">
          <label className="pt-field">
            <span className="pt-label">
              Datei (xlsx, xls oder csv · alle Tabellenblätter · bis 5 MB · wird im Browser gelesen)
            </span>
            <input type="file" accept=".xlsx,.xls,.csv" onChange={onFile} disabled={busy} />
          </label>
          <div className="pt-field" style={{ flex: '0 0 auto', justifyContent: 'flex-end' }}>
            <DsButton size="small" variant="ghost" onClick={() => setView('names')} disabled={busy}>
              Spaltennamen…
            </DsButton>
          </div>
        </div>
        <div className="pt-field">
          <span className="pt-label">
            Bisherige Fixpreise dieses Lieferanten für Artikel, die nicht in der Datei stehen
          </span>
          <DsSegmented<FixedPriceImportMissingAction>
            ariaLabel="Fixpreise für Artikel, die in der Datei fehlen"
            size="small"
            value={missingAction}
            onChange={setMissingAction}
            options={[
              { value: 'keep', label: 'bis Ablauf behalten' },
              {
                value: 'end',
                label: 'sofort beenden',
                ...(missingCount != null ? { count: missingCount } : {}),
              },
            ]}
          />
          {missingAction === 'end' && missingCount != null && missingCount > 0 && !result && (
            <span className="pt-fix-hint">
              {plural(missingCount, 'Fixpreis', 'Fixpreise')} von {supplierName.trim()} gelten ab heute nicht mehr.
            </span>
          )}
        </div>

        {error && <DsAlert severity="error">{error}</DsAlert>}
        {reading && <div className="pt-fix-empty">Lese Datei…</div>}

        {result && (
          <DsAlert severity="success">
            {plural(result.importedCount, 'Fixpreis', 'Fixpreise')} für {supplierName.trim()} übernommen.
            {result.endedCount > 0 &&
              ` ${plural(result.endedCount, 'bisheriger Fixpreis endet', 'bisherige Fixpreise enden')} heute.`}
            {result.rowCount - result.matchedCount > 0 &&
              (result.rowCount - result.matchedCount === 1
                ? ' 1 Zeile ohne Zuordnung wurde nicht übernommen.'
                : ` ${result.rowCount - result.matchedCount} Zeilen ohne Zuordnung wurden nicht übernommen.`)}
            {result.learnedColumnNames.length > 0 &&
              ` ${plural(result.learnedColumnNames.length, 'Spaltenname', 'Spaltennamen')} für künftige Uploads gemerkt.`}
          </DsAlert>
        )}
        {result && result.skippedItemIds.length > 0 && (
          <DsAlert severity="warn">
            Nicht übernommen, weil der Artikel schon vier aktive Fixpreise hat: {result.skippedItemIds.join(', ')}
          </DsAlert>
        )}

        {workbook && shown && (
          <>
            <div className="pt-import-summary">
              <span>
                {multiSheet && (
                  <>
                    {plural(workbook.sheets.length, 'Blatt', 'Blätter')}, davon {imported.length} übernommen ·{' '}
                  </>
                )}
                <b>{shown.rowCount}</b> Zeilen · <b>{shown.matchedCount}</b> Treffer ·{' '}
                <b>{notTakenCount}</b> ohne Übernahme
              </span>
              {notTakenCount > 0 && (
                <DsButton size="small" variant="ghost" onClick={downloadNotTaken}>
                  Nicht übernommene Zeilen als CSV
                </DsButton>
              )}
            </div>
            {showOutcome ? (
              <div className="pt-import-outcomes">
                Wirkung: <b>{shown.newCount}</b> neu · <b>{shown.replaceCount}</b> ersetzt ·{' '}
                <b>{shown.skipCount}</b> übersprungen
              </div>
            ) : !supplierName.trim() ? (
              <div className="pt-fix-hint">
                Die Wirkung je Zeile (neu, ersetzt, übersprungen) erscheint, sobald ein Lieferant gewählt ist.
              </div>
            ) : (
              !previewMutation.isPending && (
                <div className="pt-fix-hint">
                  Die Wirkung für diesen Lieferanten erscheint, sobald du das Feld Lieferant verlässt.
                </div>
              )
            )}
            {blocked.length > 0 && !result && (
              <DsAlert severity="warn">
                Erst die Spalten zuordnen oder das Blatt herausnehmen: {blocked.map((s) => `„${s.name}“`).join(', ')}
              </DsAlert>
            )}
          </>
        )}

        {workbook && (
          <div className="pt-import-sheets">
            {multiSheet && (
              <div className="pt-import-tabs">
                <DsTabNav
                  tabs={workbook.sheets.map((s, i) => {
                    const p = previewOf(i);
                    const out = !!s.problem || (p != null && !p.included);
                    return {
                      id: String(i),
                      label: out ? `${s.name} (aus)` : s.name,
                      count: p && p.included && !p.problem ? p.matchedCount : undefined,
                    };
                  })}
                  activeTab={String(activeSheet)}
                  onTabChange={(id) => setActiveSheet(Number(id))}
                />
              </div>
            )}
            {activeCells && (
              <PriceListSheetPanel
                key={activeSheet}
                cells={activeCells}
                sheet={previewOf(activeSheet)}
                readOnly={busy || !!result}
                showOutcome={showOutcome}
                onHeaderRow={(rowNumber) => changeSheet(activeSheet, { headerRow: rowNumber, mapping: undefined })}
                onInclude={(include) => changeSheet(activeSheet, { include })}
                onColumnField={(columnIndex, field) => onColumnField(activeSheet, columnIndex, field)}
                onEditNames={() => setView('names')}
              />
            )}
          </div>
        )}

        {learned.length > 0 && (
          <label className="ds-ck pt-import-remember">
            <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} />
            <span className="box" />
            <span>
              Von Hand geänderte Zuordnungen für künftige Uploads merken:{' '}
              {learned.map((l) => `„${l.name}“ → ${targetLabel(l.field)}`).join(' · ')}
            </span>
          </label>
        )}
      </div>
    </DsModal>
  );
}
